import EmptyState from 'components/EmptyState/EmptyState';
import WidgetLoadingAnimation from 'components/Widgets/WidgetLoadingAnimation';
import WidgetTimeUpdate from 'components/Widgets/WidgetTimeUpdate';
import 'features/Weather/assets/weatherDataDisplay.css';
import useLocation from 'hooks/useLocation';
import { useId } from 'react';
import { Container } from 'react-bootstrap';
import {
  useGetDetailedFireDangerQuery,
  useGetExtendedFireDangerQuery,
} from 'services/weather/fireDanger/fireDanger.api';
import FireDangerLocationForecast from './components/FireDangerLocationForecast/FireDangerLocationForecast';
import { getFireDangerLocationNames, getFireDangerLocations } from './utils/fireDanger.utils';
import './assets/fireDanger.scss';

const FireDanger = () => {
  const headingId = useId();
  const { locations } = useLocation();
  const weatherLocation = locations.weatherLocation;
  const isLocationLoading = !weatherLocation || weatherLocation.isLoading;
  const detailedForecastQuery = useGetDetailedFireDangerQuery(weatherLocation, {
    skip: isLocationLoading,
  });
  const extendedForecastQuery = useGetExtendedFireDangerQuery(weatherLocation, {
    skip: isLocationLoading,
  });
  // The extended endpoint projects no place name, so both sections read the names the detailed
  // payload carries for the same city ids.
  const knownLocationNames = getFireDangerLocationNames(detailedForecastQuery.data);
  const forecastSections = [
    {
      id: 'detailed',
      title: 'Detailed Forecast',
      query: detailedForecastQuery,
      isDetailed: true,
    },
    {
      id: 'extended',
      title: 'Extended Forecast',
      query: extendedForecastQuery,
      isDetailed: false,
    },
  ];

  if (isLocationLoading) {
    return <WidgetLoadingAnimation />;
  }

  return (
    <Container
      fluid
      className="fire-danger weather-data-display d-flex flex-column h-100 gap-3 px-0"
    >
      <WidgetTimeUpdate
        route="getExtendedFireDanger"
        updatedAt={
          Math.max(
            detailedForecastQuery.fulfilledTimeStamp ?? 0,
            extendedForecastQuery.fulfilledTimeStamp ?? 0,
          ) || null
        }
      />
      <div className="weather-data-display-heading border-bottom pb-2">
        <h2 className="weather-data-display-title m-0 fw-bold lh-sm">Fire Danger</h2>
      </div>
      <div className="fire-danger-scroll flex-grow-1 overflow-auto">
        <div className="d-flex flex-column gap-4 pb-3">
          {forecastSections.map(({ id, title, query, isDetailed }) => {
            const sectionHeadingId = `${headingId}-${id}`;
            const { data, isFetching, isError } = query;
            const forecastLocations = getFireDangerLocations(data, knownLocationNames);
            const hasReports = forecastLocations.length > 0;
            const isLoadingReports = isFetching && !hasReports;

            return (
              <section
                key={id}
                aria-labelledby={sectionHeadingId}
                className="d-flex w-100 flex-column gap-3"
              >
                <div className="d-flex align-items-start justify-content-between gap-3">
                  <h3 id={sectionHeadingId} className="fire-danger-section-title m-0 fw-bold lh-sm">
                    {title}
                  </h3>
                  {isFetching && hasReports && (
                    <span className="fire-danger-status small" role="status">
                      Updating...
                    </span>
                  )}
                </div>

                {isLoadingReports && <WidgetLoadingAnimation />}

                {isError && (
                  <p className="m-0" role="alert">
                    {hasReports
                      ? 'Unable to update these reports. Showing the last available results.'
                      : 'Unable to load these reports. Please try again later.'}
                  </p>
                )}

                {/*
                  No title: the section heading above already names this forecast,
                  and repeating it would paint the name twice and nest a second
                  heading inside the section the h3 labels.
                */}
                {!isLoadingReports && !isError && !hasReports && (
                  <EmptyState message="No fire danger reports are available for this location." />
                )}

                {forecastLocations.map((location) => (
                  <FireDangerLocationForecast
                    key={location.id}
                    location={location}
                    isDetailed={isDetailed}
                  />
                ))}
              </section>
            );
          })}
        </div>
      </div>
    </Container>
  );
};

export default FireDanger;
